// Shared design tokens consumed by both Tailwind config (root) and runtime code.
// Kept inside src/ so Create React App allows importing it from components.

const charcoal = {
	'050': '#D7DADD',
	'100': '#BCC1C6',
	'200': '#9BA2A9',
	'300': '#7A848D',
	'400': '#586571',
	'500': '#374654',
	'600': '#2E3A46',
	'700': '#252F38',
	'800': '#1C232A',
	'900': '#12171C',
}

const sun = {
	'050': '#FFF9E7',
	'100': '#FFF6D7',
	'200': '#FFF1C3',
	'300': '#FFECAF',
	'400': '#FFE89B',
	'500': '#FFE387',
	'600': '#D4BD71',
	'700': '#AA975A',
	'800': '#807244',
	'900': '#554C2D',
}

const colors = {
	transparent: 'transparent',
	white: '#fff',
	charcoal,
	// Semantic alias: the single place the app-wide placeholder shade is chosen.
	placeholder: charcoal['200'],
	algae: {
		'050': '#E4F9EF',
		'100': '#D2F5E4',
		'200': '#BBF0D6',
		'300': '#A4EBC9',
		'400': '#8EE6BB',
		'500': '#77E1AE',
		'600': '#63BB91',
		'700': '#4F9674',
		'800': '#3C7157',
		'900': '#284B3A',
	},
	success: {
		'050': '#E6EFD6',
		'100': '#D6E4BB',
		'200': '#C1D798',
		'300': '#ADCA76',
		'400': '#98BC54',
		'500': '#84AF32',
		'600': '#6E922A',
		'700': '#587521',
		'800': '#425819',
		'900': '#2C3A11',
	},
	error: {
		'050': '#FFDCDC',
		'100': '#FFC5C4',
		'200': '#FFA8A7',
		'300': '#FF8B8A',
		'400': '#FF6E6C',
		'500': '#FF514F',
		'600': '#D44442',
		'700': '#AA3635',
		'800': '#802928',
		'900': '#551B1A',
	},
	sun,
	// Warning shares the sun scale so warning surfaces match the sun banners.
	warning: sun,
	ozone: {
		'050': '#D2F7FF',
		'500': '#0AB7FF',
	},
	fire: {
		'050': '#FFF5F0',
		'100': '#FFE0CE',
		'200': '#FFD4B8',
		'300': '#FFC8A2',
		'400': '#FFBC8C',
		'500': '#FFB076',
		'600': '#FFA675',
		'700': '#FF8E57',
		'800': '#FF7426',
		'900': '#E65100',
	},
}

const spacing = {
	'sm': '7px',
	'md': '9px',
	'lg': '11px',
	'xl': '13px',
	'2xl': '17px',
	'3xl': '25px',
}

const borderRadius = {
	none: '0',
	sm: '6px',
	DEFAULT: '6px',
	md: '6px',
	lg: '8px',
	full: '9999px',
}

const fontFamily = {
	sans: ['"IBM Plex Sans"', 'sans-serif'],
	serif: ['Quicksand', 'serif'],
	mono: ['"IBM Plex Mono"', 'sans-serif'],
}

// Deliberate override (not an extension) of Tailwind's default 100-900 scale: only the
// weights IBM Plex Sans is loaded with (self-hosted in src/assets/fonts/ibm-plex/ibm-plex.css,
// #766) and the app actually uses. Thin, light, extrabold and black are
// intentionally absent, so a typo produces the guard-test failure (fontWeights.test.ts)
// instead of silently compiling to no CSS.
const fontWeight = {
	normal: '400',
	medium: '500',
	semibold: '600',
	bold: '700',
}

// Semantic roles (size, weight, line-height), not raw weights, so a component asks for what
// it is ("table-head", "label") rather than hand-rolling text-*/font-* classes. Emitted as
// Tailwind component classes (`.type-<role>`) by the local plugin in tailwind.config.js.
// `button` only carries a weight because Button.tsx already owns its own size/padding scale.
const typography = {
	'page-title': { fontSize: '1.5rem', lineHeight: '2rem', fontWeight: fontWeight.normal },
	'section-heading': { fontSize: '1.125rem', lineHeight: '1.75rem', fontWeight: fontWeight.normal },
	'card-title': { fontSize: '1rem', lineHeight: '1.5rem', fontWeight: fontWeight.normal },
	'label': { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: fontWeight.normal },
	'table-head': { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: fontWeight.normal },
	'button': { fontWeight: fontWeight.normal },
	'body': { fontSize: '1rem', lineHeight: '1.5rem', fontWeight: fontWeight.normal },
	'caption': { fontSize: '0.75rem', lineHeight: '1rem', fontWeight: fontWeight.normal },
	// Numbers only (see NumericText): Plex Mono's slashed zero tells 0 from O. Size and line-height
	// are inherited so signs, currency symbols and units stay Plex Sans at the same size.
	'numeric': { fontFamily: fontFamily.mono.join(', '), fontWeight: fontWeight.normal, fontVariantNumeric: 'slashed-zero' },
}

const ringWidth = {
	0: '0px',
}

// Canonical inline text link style: inherits the surrounding text colour so it reads correctly
// on any background, underlines to signal it's a link, and only turns algae-500 on hover.
// Emitted as the `.text-link` component class by the local plugin in tailwind.config.js.
// Compose with TEXT_LINK_FOCUS_RING (a11yFocus.ts) for keyboard focus styling.
const textLink = {
	textDecorationLine: 'underline',
	color: 'inherit',
	cursor: 'pointer',
	'&:hover': {
		color: colors.algae['500'],
	},
}

module.exports = {
	colors,
	spacing,
	borderRadius,
	fontFamily,
	fontWeight,
	typography,
	ringWidth,
	textLink,
}
